import React from 'react';
import {
  formatDateValue,
  getCurrentDateValue,
  parseRawDateValue,
  RawDateValue,
} from '../../../utils/date-value';
import { Controller, useForm } from 'react-hook-form';
import { getExtendedDeliveryOrderExportUrl } from '../../../utils/common.utils';
import { InputWrapper } from '../../common/input-wrapper';
import { CustomProvider, Button, DateRangePicker } from 'rsuite';
import { ruRU } from 'rsuite/locales';
import { isCorrectDate } from './reconciliation-act-modal';
import { Select } from '../../common/select.component';
import { Checkbox } from '../../common/checkbox';
import { required } from '../../../utils/validations';
import { useSuppliers } from '../../../api/suppliers';
import { CleanDeliveryOrder } from '../../../export-types/cleaned-types';

type Period =
  | 'all_time'
  | 'week'
  | 'month'
  | 'year'
  | 'last_week'
  | 'last_month'
  | 'last_year'
  | 'range';

const periods: { title: string; id: Period }[] = [
  { title: 'За все время', id: 'all_time' },
  { title: 'Текущая неделя', id: 'week' },
  { title: 'Текущий месяц', id: 'month' },
  { title: 'Этот год', id: 'year' },
  { title: 'Прошлая неделя', id: 'last_week' },
  { title: 'Прошлый месяц', id: 'last_month' },
  { title: 'Прошлый год', id: 'last_year' },
  { title: 'Выбрать диапазон', id: 'range' },
];

// Расширенный отчёт всегда включает доставки в этих статусах:
// — В пути на склад РФ
// — На складе РФ (приёмка обычная и retail)
// — Переданные в ТК
// — Полученные
const REPORT_STATUSES: CleanDeliveryOrder['status'][] = [
  'sentToRussia',
  'receivedInRussia',
  'receivedInRetailRussia',
  'sentToRecipient',
  'delivered',
];
const REPORT_TITLE = 'Расширенный отчёт по доставкам';

type Props = {
  close: () => void;
};

type Form = {
  suppliers: number[];
  period: Period;
  range?: RawDateValue[];
  byPaymentDate: boolean;
};

export const ExtendedDeliveryReportModal: React.FC<Props> = ({ close }) => {
  const { data: allSuppliers } = useSuppliers();

  const {
    control,
    formState: { isValid, errors },
    watch,
    trigger,
  } = useForm<Form>({
    mode: 'all',
    defaultValues: {
      period: 'week',
      byPaymentDate: false,
    },
  });

  const [suppliers, period, range, byPaymentDate] = watch([
    'suppliers',
    'period',
    'range',
    'byPaymentDate',
  ]);

  React.useEffect(() => {
    trigger(['range']);
  }, [trigger, range]);

  const reportLink: string | null = React.useMemo<string | null>(() => {
    if (!isValid || !suppliers || suppliers.length === 0) return null;
    let periodQuery: { start?: string; end?: string } = {};

    switch (period) {
      case 'range':
        if (!range) return null;
        const [customStart, customEnd] = range;
        if (!customStart && !customEnd) return null;

        periodQuery = {
          start: customStart
            ? parseRawDateValue(customStart).startOf('day').valueOf().toString()
            : undefined,
          end: customEnd
            ? parseRawDateValue(customEnd).endOf('day').valueOf().toString()
            : undefined,
        };
        break;
      case 'week':
        periodQuery = {
          start: getCurrentDateValue().startOf('isoWeek').valueOf().toString(),
        };
        break;
      case 'month':
        periodQuery = {
          start: getCurrentDateValue().startOf('month').valueOf().toString(),
        };
        break;
      case 'year':
        periodQuery = {
          start: getCurrentDateValue().startOf('year').valueOf().toString(),
        };
        break;
      case 'last_week':
        periodQuery = {
          start: getCurrentDateValue()
            .subtract(1, 'week')
            .startOf('isoWeek')
            .valueOf()
            .toString(),
          end: getCurrentDateValue()
            .subtract(1, 'week')
            .endOf('isoWeek')
            .valueOf()
            .toString(),
        };
        break;
      case 'last_month':
        periodQuery = {
          start: getCurrentDateValue()
            .subtract(1, 'month')
            .startOf('month')
            .valueOf()
            .toString(),
          end: getCurrentDateValue()
            .subtract(1, 'month')
            .endOf('month')
            .valueOf()
            .toString(),
        };
        break;
      case 'last_year':
        periodQuery = {
          start: getCurrentDateValue()
            .subtract(1, 'year')
            .startOf('year')
            .valueOf()
            .toString(),
          end: getCurrentDateValue()
            .subtract(1, 'year')
            .endOf('year')
            .valueOf()
            .toString(),
        };
      default:
        break;
    }

    return getExtendedDeliveryOrderExportUrl({
      title: REPORT_TITLE,
      suppliers,
      status: REPORT_STATUSES,
      byPaymentDate,
      ...periodQuery,
    });
  }, [period, isValid, range, suppliers, byPaymentDate]);

  return (
    <CustomProvider locale={ruRU}>
      <div className="modal-dialog">
        <form className="modal-content">
          <div className="modal-body pb-0">
            <div className="d-flex flex-row justify-content-between align-items-center">
              <h3>Расширенный отчёт по доставкам</h3>
              <div className="btn p-0" onClick={close}>
                <i className="bi bi-x fs-36 color-gray-600" />
              </div>
            </div>
            <Controller
              name="suppliers"
              control={control}
              rules={required}
              render={({ field }) => (
                <Select
                  className="mb-3"
                  title="Поставщик"
                  placeholder="Выберите поставщиков"
                  isMulti
                  required
                  options={
                    allSuppliers?.map(({ id, name }) => ({
                      value: id,
                      label: name,
                    })) ?? []
                  }
                  onChange={(selectedOption) => field.onChange(selectedOption)}
                  value={field.value}
                  error={errors.suppliers?.message}
                />
              )}
            />
            <Controller
              name="period"
              control={control}
              rules={{
                required: 'Укажите период',
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    required
                    title="Выберите период"
                    error={fieldState.error?.message}
                    className="mb-3"
                  >
                    <select className="form-select" {...field}>
                      {periods.map((item) => {
                        return (
                          <option value={item.id} key={item.id}>
                            {item.title}
                          </option>
                        );
                      })}
                    </select>
                  </InputWrapper>
                );
              }}
            />
            {period === 'range' && (
              <Controller
                name="range"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value || value.length !== 2) return true;

                    const [start, end] = value;

                    if (!isCorrectDate(start) || !isCorrectDate(end)) {
                      return 'Укажите корректные даты';
                    }

                    if (
                      parseRawDateValue(start).isAfter(parseRawDateValue(end))
                    ) {
                      return 'Дата начала не должна быть позже даты окончания';
                    }

                    return true;
                  },
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      title="Выберите период"
                      error={fieldState.error?.message}
                      className="mb-2 w-full text-base-lh-100"
                    >
                      <DateRangePicker
                        className={'w-100'}
                        format={'dd.MM.yyyy'}
                        isoWeek
                        showOneCalendar
                        onChange={(range) => {
                          if (range == null) field.onChange([]);
                          else {
                            field.onChange(
                              range.map((date) => formatDateValue(date)),
                            );
                          }
                        }}
                      />
                    </InputWrapper>
                  );
                }}
              />
            )}
            <Controller
              name="byPaymentDate"
              control={control}
              render={({ field }) => (
                <Checkbox
                  className="mb-3"
                  title="По дате оплаты"
                  checked={field.value}
                  onChange={({ target: { checked } }) =>
                    field.onChange(checked)
                  }
                />
              )}
            />
          </div>
          <div className="modal-body pt-0 text-secondary">
            * Расширенный отчёт содержит детализацию финансов, дату оплаты, курс
            доллара и ответственного менеджера.
          </div>
          <div className="modal-footer justify-content-end">
            {reportLink ? (
              <Button
                as="a"
                href={reportLink}
                target="_blank"
                className="btn btn-success"
              >
                Скачать в Excel
              </Button>
            ) : (
              <Button as="button" disabled={true} className="btn btn-success">
                Скачать в Excel
              </Button>
            )}
          </div>
        </form>
      </div>
    </CustomProvider>
  );
};
